// Create a new file: src/components/PaymentMethodForm.js
import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';

const PaymentMethodForm = ({ onComplete, onCancel, error: externalError }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [saveAsDefault, setSaveAsDefault] = useState(true);
  const stripe = useStripe();
  const elements = useElements();
  const { config } = useConfig();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      
      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      // Save payment method
      const response = await axios.post('/api/users/payment-methods', {
        paymentMethodId: paymentMethod.id,
        makeDefault: saveAsDefault
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        onComplete(response.data.paymentMethods);
      } else {
        throw new Error('Failed to save payment method');
      }
    } catch (error) {
      console.error('Payment method error:', error);
      setError(
        error.response?.data?.error || 
        error.message || 
        'An error occurred while adding your payment method. Please try again.'
      );
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      {(error || externalError) && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || externalError}</AlertDescription>
        </Alert>
      )}

      <div className="mb-4 p-4 border rounded">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>

      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={saveAsDefault}
            onChange={(e) => setSaveAsDefault(e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">Set as default payment method</span>
        </label>
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className={`px-4 py-2 rounded-md text-white font-medium
            ${(!stripe || processing) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
          style={{ backgroundColor: config.colors.primary }}
        >
          {processing ? 'Processing...' : 'Add Payment Method'}
        </button>
      </div>
    </form>
  );
};

export default PaymentMethodForm;